/**
 * MAS Direct template. All state, loaders, handlers, and effects in useMASDirectTemplateCore.
 */
import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useParams } from 'react-router-dom';
import { useStore } from 'hooks/useStore';
import { useMASDirectTemplateCore } from './useMASDirectTemplateCore';
import { TemplateLayout } from '../../components/layout/TemplateLayout';
import { MASDirectTemplateContent } from './MASDirectTemplateContent';
import NotesPanel from '../../components/NotesPanel';
import { MarketTrendsModal } from '../../components/modals/MarketTrendsModal';
import RequestModificationModal from '../../components/RequestModificationModal';
import ApproveConfirmationDialog from '../../components/ApproveConfirmationDialog';
import classes from '../../bidAnalysisTemplate.module.scss';

const MASDirectTemplate = observer(() => {
  const { bidId } = useParams<{ bidId: string }>();
  const { BidAnalysisStore } = useStore();
  const core = useMASDirectTemplateCore();

  if (core.loading || BidAnalysisStore.isLoadingBidDetails) {
    return (
      <div className={classes.templateWrapper}>
        <div className={classes.loadingContainer}>
          <div className={classes.loadingSpinner}>
            <ProgressSpinner style={{ width: '100%', height: '100%' }} />
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!core.bidDetails && bidId) {
    return (
      <div className={classes.templateWrapper}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: '3rem', color: '#f59e0b' }}
            />
            <h3 style={{ marginTop: '1rem' }}>Bid Not Found</h3>
            <p style={{ color: '#6b7280' }}>Unable to load bid details for ID: {bidId}</p>
            <Button
              label="Go to Dashboard"
              icon="pi pi-arrow-left"
              onClick={() => core.requestNavigation('/bdportal/bid-analysis/dashboard')}
              style={{ marginTop: '1rem' }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (core.templateType !== 'nc' && !core.templateData) {
    return (
      <div className={classes.templateWrapper}>
        <div className={classes.loadingContainer}>
          <div className={classes.loadingSpinner}>
            <ProgressSpinner style={{ width: '100%', height: '100%' }} />
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.templateWrapper}>
      <TemplateLayout {...core.layoutProps}>
        <MASDirectTemplateContent ctx={core.masCtx} />
      </TemplateLayout>
      <NotesPanel
        ref={core.notesPanelProps.ref}
        bidId={core.notesPanelProps.bidId}
        sectionName={core.notesPanelProps.sectionName}
        notes={core.notesPanelProps.notes}
        onNotesChange={core.notesPanelProps.onNotesChange}
        currentUser={core.notesPanelProps.currentUser}
        readOnly={core.notesPanelProps.readOnly}
      />
      <MarketTrendsModal {...core.chartModalProps} />
      {core.managerModalsProps.isManagerReviewMode && core.managerModalsProps.bidId && (
        <>
          <RequestModificationModal
            visible={core.managerModalsProps.requestModification.visible}
            onHide={core.managerModalsProps.requestModification.onHide}
            navigationItems={core.managerModalsProps.requestModification.navigationItems}
            bidId={core.managerModalsProps.requestModification.bidId}
            onSuccess={core.managerModalsProps.requestModification.onSuccess}
            bidStatus={core.managerModalsProps.requestModification.bidStatus}
          />
          <ApproveConfirmationDialog
            visible={core.managerModalsProps.approve.visible}
            onHide={core.managerModalsProps.approve.onHide}
            bidId={core.managerModalsProps.approve.bidId}
            onSuccess={core.managerModalsProps.approve.onSuccess}
            bidStatus={core.managerModalsProps.approve.bidStatus}
          />
        </>
      )}
    </div>
  );
});

export default MASDirectTemplate;
